/**
 * Plan units (pu) — the canonical, exact, integer unit of all building
 * geometry (approved Phase 7 decision 1).
 *
 *   1 pu = 1/2032 inch = 1/80 mm        (2032 = 16 × 127)
 *
 * It is the coarsest unit that represents BOTH 1/16 inch (127 pu) and
 * 0.1 mm (8 pu) exactly, so typed imperial dimensions round-trip exactly
 * today and metric can be added later over the same coordinates. Users
 * never see plan units: all display goes through the formatters below.
 *
 * Pure TypeScript: no DOM, no React, no platform dependencies.
 */

export const PU_PER_INCH = 2032;
export const PU_PER_FOOT = 12 * PU_PER_INCH; // 24,384
export const PU_PER_SIXTEENTH = PU_PER_INCH / 16; // 127
export const PU_PER_MM = 80;

/** 1 sqft in square plan units (exact integer). */
export const SQ_PU_PER_SQFT = PU_PER_FOOT * PU_PER_FOOT; // 594,579,456

/** Guard: coordinates beyond ±410 ft from the plan origin are refused so
 *  integer products stay far inside Number.MAX_SAFE_INTEGER. */
export const MAX_COORDINATE_PU = 10_000_000;

export type UnitSystem = "imperial" | "metric";

export function inchesToPu(inches: number): number {
  return Math.round(inches * PU_PER_INCH);
}

export function feetToPu(feet: number): number {
  return Math.round(feet * PU_PER_FOOT);
}

/** Exact integer conversion: inches (whole) → pu. */
export function wholeInchesToPu(inches: number): number {
  return inches * PU_PER_INCH;
}

/** Half-up integer division for non-negative and negative numerators. */
export function roundDiv(numerator: number, denominator: number): number {
  if (denominator <= 0) throw new Error("denominator must be positive");
  if (numerator >= 0) return Math.floor((numerator + denominator / 2) / denominator);
  return -Math.floor((-numerator + denominator / 2) / denominator);
}

/** pu → whole inches (rounded half-up). */
export function puToInchesRounded(pu: number): number {
  return roundDiv(pu, PU_PER_INCH);
}

/** Square pu → sqft × 100 (rounded half-up). */
export function sqPuToSqftX100(sqPu: number): number {
  return roundDiv(sqPu * 100, SQ_PU_PER_SQFT);
}

/** Linear pu → feet × 100 (rounded half-up). */
export function puToFeetX100(pu: number): number {
  return roundDiv(pu * 100, PU_PER_FOOT);
}

export type LengthParseResult = { ok: true; pu: number } | { ok: false; error: string };

const FRACTION = /^(\d+)\/(\d+)$/;

export interface LengthParseOptions {
  /**
   * What a BARE number means — one typed with no ' or " and no unit word.
   *
   * 'feet' is the default and the rule everywhere in the Design workspace:
   * on a floor plan a bare number overwhelmingly means feet ("6" is a six
   * foot wall, not a six inch one). Explicit marks always win, so 6" is
   * still six inches.
   */
  bare?: "feet" | "inches";
}

/** True when the (normalised) text carries an explicit foot or inch mark. */
function hasUnitMark(s: string): boolean {
  return s.includes("'") || s.includes('"');
}

/**
 * Parse a user-typed length into plan units. Accepts (imperial):
 *   6      → 6 FEET      (bare numbers are feet; see LengthParseOptions)
 *   6.5    → 6 ft 6 in
 *   6'     6' 6"   6'-6"   6 ft 6 in   6 6   (six foot six)
 *   6"     150"    4.5"    3 1/2   3/8      (inches: a mark, or a fraction)
 *   12.5'  5' 3 3/8"      -0' 6"           (negative allowed)
 * Metric (when unitSystem = 'metric'): 3000 mm, 3 m, 2.5m, 300cm, 3000 (mm).
 *
 * FRACTIONS ARE INCHES even bare: "3 1/2" and "3/8" are how a tape measure
 * is read, and nobody writes three and a half FEET that way — they write
 * 3.5 or 3' 6". That is the one documented exception to bare-means-feet.
 */
export function parseLength(
  raw: string,
  unitSystem: UnitSystem = "imperial",
  options: LengthParseOptions = {},
): LengthParseResult {
  let s = raw.trim().toLowerCase().replace(/[′’]/g, "'").replace(/[″”]/g, '"');
  if (s === "") return { ok: false, error: "Enter a length." };
  let sign = 1;
  if (s.startsWith("-")) {
    sign = -1;
    s = s.slice(1).trim();
  }
  if (unitSystem === "metric") {
    const m = /^(\d+(?:\.\d+)?)\s*(mm|cm|m)?$/.exec(s);
    if (!m) return { ok: false, error: "Enter a length like 3000 mm, 300 cm or 3 m." };
    const value = Number.parseFloat(m[1]);
    const perUnit = m[2] === "m" ? PU_PER_MM * 1000 : m[2] === "cm" ? PU_PER_MM * 10 : PU_PER_MM;
    return { ok: true, pu: sign * Math.round(value * perUnit) };
  }
  // Normalise word units to symbols.
  s = s
    .replace(/\s*(feet|foot|ft)\.?\s*/g, "' ")
    .replace(/\s*(inches|inch|in)\.?\s*/g, '" ')
    .replace(/'\s*-\s*/g, "' ")
    .replace(/\s+/g, " ")
    .trim();

  // ---- Bare input: no ' and no " anywhere. -------------------------------
  if (!hasUnitMark(s)) {
    const bare = parseBare(s, options.bare ?? "feet");
    if (bare === null) {
      return { ok: false, error: `Could not read "${raw}". Try 6, 6' 6" or 150".` };
    }
    return finish(sign * bare, raw);
  }

  let totalPu = 0;
  let rest = s;
  // feet part
  const feetMatch = /^(\d+(?:\.\d+)?)\s*'/.exec(rest);
  if (feetMatch) {
    const feet = feetMatch[1];
    totalPu += decimalTimes(feet, PU_PER_FOOT);
    rest = rest.slice(feetMatch[0].length).trim();
  }
  // inches part: "3 3/8"", "6"", "4.5"", "3/8"", or a bare remainder after
  // a feet part ("6' 6" is six foot six).
  if (rest !== "") {
    const inchMatch = /^(?:(\d+)\s+)?(\d+\/\d+|\d+(?:\.\d+)?)\s*"?$/.exec(rest);
    if (!inchMatch) {
      return { ok: false, error: `Could not read "${raw}". Try 6, 6' 6" or 150".` };
    }
    const [, wholePart, lastPart] = inchMatch;
    if (wholePart !== undefined) totalPu += Number.parseInt(wholePart, 10) * PU_PER_INCH;
    const frac = FRACTION.exec(lastPart);
    if (frac) {
      const num = Number.parseInt(frac[1], 10);
      const den = Number.parseInt(frac[2], 10);
      if (den === 0) return { ok: false, error: "Fraction denominator cannot be zero." };
      totalPu += roundDiv(num * PU_PER_INCH, den);
    } else {
      totalPu += decimalTimes(lastPart, PU_PER_INCH);
    }
  }
  if (!feetMatch && rest === "") return { ok: false, error: "Enter a length." };
  return finish(sign * totalPu, raw);
}

/**
 * A number with no marks at all. Returns plan units, or null if it is not a
 * length this parser understands.
 */
function parseBare(s: string, bare: "feet" | "inches"): number | null {
  const perUnit = bare === "feet" ? PU_PER_FOOT : PU_PER_INCH;
  // A plain number: the common case, and the whole point of the rule.
  if (/^\d+(?:\.\d+)?$/.test(s)) return decimalTimes(s, perUnit);
  // A fraction, alone or after a whole number — tape-measure notation, so
  // inches whichever way the caller reads plain numbers.
  const fraction = /^(?:(\d+)\s+)?(\d+)\/(\d+)$/.exec(s);
  if (fraction) {
    const [, whole, num, den] = fraction;
    const denominator = Number.parseInt(den, 10);
    if (denominator === 0) return null;
    const wholePu = whole === undefined ? 0 : Number.parseInt(whole, 10) * PU_PER_INCH;
    return wholePu + roundDiv(Number.parseInt(num, 10) * PU_PER_INCH, denominator);
  }
  // "6 6" — two whole numbers, said the way a carpenter says it: six foot
  // six. Only when bare numbers are feet; otherwise it is meaningless.
  const feetInches = /^(\d+)\s+(\d+)$/.exec(s);
  if (feetInches && bare === "feet") {
    return Number.parseInt(feetInches[1], 10) * PU_PER_FOOT + Number.parseInt(feetInches[2], 10) * PU_PER_INCH;
  }
  return null;
}

function finish(pu: number, raw: string): LengthParseResult {
  if (!Number.isSafeInteger(pu) || Math.abs(pu) > MAX_COORDINATE_PU * 2) {
    return { ok: false, error: `${raw.trim()} is too large.` };
  }
  return { ok: true, pu };
}

/** "12.5" × perUnit using integer arithmetic on the decimal string (exact
 *  when the decimal has ≤ 6 digits; half-up rounded to pu otherwise). */
function decimalTimes(decimal: string, perUnit: number): number {
  const [whole, frac = ""] = decimal.split(".");
  const fracDigits = frac.slice(0, 9);
  const scale = 10 ** fracDigits.length;
  const scaled = Number.parseInt(whole, 10) * scale + (fracDigits === "" ? 0 : Number.parseInt(fracDigits, 10));
  return roundDiv(scaled * perUnit, scale);
}

/** Parse "30' x 40'" / "12 × 14" / "30'x40'" into two lengths. */
export function parseDimensionPair(
  raw: string,
  unitSystem: UnitSystem = "imperial",
  options: LengthParseOptions = {},
): { ok: true; a: number; b: number } | { ok: false; error: string } {
  const parts = raw.split(/\s*[x×X*]\s*/);
  if (parts.length !== 2) return { ok: false, error: "Enter two sizes like 12' × 14'." };
  const a = parseLength(parts[0], unitSystem, options);
  const b = parseLength(parts[1], unitSystem, options);
  if (!a.ok) return a;
  if (!b.ok) return b;
  if (a.pu <= 0 || b.pu <= 0) return { ok: false, error: "Sizes must be positive." };
  return { ok: true, a: a.pu, b: b.pu };
}

export interface FormatLengthOptions {
  /** Imperial display denominator: 16 → sixteenths (default), 8, 4, 2, 1. */
  denominator?: number;
  /** 'ft-in' (12' 6") default; 'in' (150"). */
  style?: "ft-in" | "in";
}

/**
 * Format plan units for display: 304800 → 12' 6"; 9144 → 4 1/2";
 * 730,000 → 29' 11 1/4". Rounds half-up to the display denominator.
 */
export function formatLength(pu: number, unitSystem: UnitSystem = "imperial", options: FormatLengthOptions = {}): string {
  if (unitSystem === "metric") {
    const mm = roundDiv(pu, PU_PER_MM);
    return `${mm.toLocaleString("en-US")} mm`;
  }
  const den = options.denominator ?? 16;
  const sign = pu < 0 ? "-" : "";
  const abs = Math.abs(pu);
  // total in units of 1/den inch, rounded half up
  const unitsPerInch = den;
  const total = roundDiv(abs * unitsPerInch, PU_PER_INCH);
  let inchesWhole = Math.floor(total / unitsPerInch);
  let num = total % unitsPerInch;
  let d = unitsPerInch;
  while (num > 0 && num % 2 === 0 && d > 1) {
    num /= 2;
    d /= 2;
  }
  const fracText = num > 0 ? `${num}/${d}` : "";
  if (options.style === "in") {
    const body = fracText ? (inchesWhole > 0 ? `${inchesWhole} ${fracText}"` : `${fracText}"`) : `${inchesWhole}"`;
    return sign + body;
  }
  const feet = Math.floor(inchesWhole / 12);
  inchesWhole -= feet * 12;
  if (feet === 0) {
    const body = fracText ? (inchesWhole > 0 ? `${inchesWhole} ${fracText}"` : `${fracText}"`) : `${inchesWhole}"`;
    return sign + body;
  }
  const inchText = fracText ? `${inchesWhole} ${fracText}"` : `${inchesWhole}"`;
  return `${sign}${feet}' ${inchText}`;
}

/** Format an area in sqft × 100 (e.g. 18750 → "187.5 sqft"). */
export function formatAreaX100(sqftX100: number | null): string {
  if (sqftX100 === null) return "—";
  const whole = Math.floor(sqftX100 / 100);
  const frac = sqftX100 % 100;
  const text = frac === 0 ? `${whole}` : `${whole}.${frac.toString().padStart(2, "0")}`.replace(/0$/, "");
  return `${text} sqft`;
}

/**
 * The one-line explanation of the typing convention, for placeholder and
 * help text. Written once so every field says the same thing.
 */
export const LENGTH_INPUT_HINT = `6 = 6 ft · 6" = 6 in · 6' 6" = 6 ft 6 in`;

/** Angle helper for display only (degrees, 0 = east, counter-clockwise). */
export function angleDegrees(dx: number, dy: number): number {
  const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}
